// utils for the Preference row UI
import { alpha, darken, SxProps, Theme } from '@mui/material/styles';
import { expandShortHex, getContrastingText } from './preferenceColor';

type RowStyles = {
  rowSx: SxProps<Theme>;
  textSx?: SxProps<Theme>;
  iconSx?: SxProps<Theme>;
};

/**
 * Compute sx for a preference row given an optional hex color.
 * Default (no color): transparent bg, border color token, no text overrides.
 * With color: overrides only the keys that change.
 */
export function getPreferenceRowStyles(color?: string | null): RowStyles {
  const base = expandShortHex(color ?? '') || '';

  // base/default case: no color provided
  const rowSx: SxProps<Theme> = {
    p: 1,
    bgcolor: 'transparent',
    border: '1px solid var(--border-color)',
    borderRadius: 1,
  };

  if (!base) {
    return {
      rowSx,
      textSx: undefined,
      iconSx: undefined,
    };
  }

  // differences when base color is defined
  const rowText = getContrastingText(base);
  const hoverBg = alpha(base, 0.85);
  const border = `1px solid ${darken(base, 0.2)}`;
  const isLightText = rowText.toLowerCase().startsWith('#fff');
  const iconHoverOverlay = isLightText ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)';

  return {
    rowSx: {
      ...rowSx,
      bgcolor: base,
      color: rowText,
      border,
      '&:hover': { bgcolor: hoverBg },
      '& .MuiSvgIcon-root': { color: rowText },
    },
    textSx: { color: rowText },
    iconSx: { color: rowText, '&:hover': { bgcolor: iconHoverOverlay } },
  };
}
